import keys from '@debbie/common/dist/cache/keys.js'
import { Case, getCollectorOnCase } from '@debbie/common/dist/cases/index.js'
import {
  PaymentPlanStatus,
  Plan,
} from '@debbie/common/dist/economy/payment-plans/index.js'
import { PermissionLevels } from '@debbie/common/dist/index.js'
import { UserRole } from '@debbie/common/dist/users/roles.js'
import { useMutation } from '@debbie/common/dist/util/hooks.js'
import * as paymentPlansAPI from '@debbie/cortex/dist/api/payment-plans.js'
import { useCollector } from '@debbie/cortex/dist/collectors/index.js'
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js'
import { h } from '@debbie/cortex/dist/index.js'
import { paymentPlansEntity } from '@debbie/cortex/dist/paymentPlans/index.js'
import q from '@debbie/cortex/dist/query.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import ArrowLeft from '@debbie/iris/dist/components/Icons/ArrowLeft.js'
import ArrowRight from '@debbie/iris/dist/components/Icons/ArrowRight.js'
import Feather from '@debbie/iris/dist/components/Icons/Feather.js'
import { Button, Panel } from '@debbie/iris/dist/components/index.js'
import React from 'react'
import { useSelector } from 'react-redux'
import { styled } from 'styled-components'

interface Props {
  meRole: UserRole
  singleCase: Case
  paymentPlan: Plan
}

const SigningStep = (props: Props) => {
  const { meRole, singleCase, paymentPlan } = props

  const deletePlan = useMutation(
    async () => {
      await paymentPlansAPI.del(paymentPlan.paymentPlanId)
      q.invalidate(
        keys.write.modify(paymentPlansEntity, paymentPlan.paymentPlanId),
      )
    },
    {
      onError: (error) => {
        console.error(error)
        alert('An error occured. Please try again.')
      },
    },
  )
  const sign = useMutation(
    async () => {
      const link = await paymentPlansAPI.getSigningLink(
        paymentPlan.paymentPlanId,
        window.location.href,
        window.location.href,
      )

      location.replace(link.url)
    },
    {
      onError: (error) => {
        console.error(error)
        alert('Error. Please try again later.')
      },
    },
  )

  const creditorLoadable = useCreditor(singleCase?.creditorId)
  const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null

  const collectorLoadable = useCollector(
    (singleCase &&
      creditor &&
      getCollectorOnCase(
        singleCase.type,
        singleCase.collectorId,
        creditor.identityCollectorId,
        creditor.defaultCollectorId,
      )) ||
      undefined,
  )
  const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null

  const lang = useSelector((s: RootState) => s.lang)

  return (
    <>
      {paymentPlan &&
        paymentPlan.status !== PaymentPlanStatus.ACTIVE &&
        paymentPlan.createdByMe && (
          <BackLink
            type="button"
            disabled={deletePlan.isLoading}
            onClick={() => deletePlan.mutate()}
          >
            <ArrowLeft />
            <span>{lang.debtorWebPaymentPlans.chooseDifferentPlan}</span>
          </BackLink>
        )}
      <Panel header={lang.debtorWebPaymentPlans.agreementTitle}>
        <SigningContent>
          <SigningIcon aria-hidden>
            <Feather />
          </SigningIcon>

          <SigningText>
            {lang.debtorWebPaymentPlans.signTextOne}{' '}
            {collector && <CollectorName>{collector.name}</CollectorName>}{' '}
            {meRole && meRole === PermissionLevels.alternativeContact
              ? lang.debtorWebPaymentPlans.signTextTwoAlternativeContact
              : lang.debtorWebPaymentPlans.signTextTwoDebtor}
          </SigningText>

          <SigningCta>
            <Button
              block
              onClick={() => sign.mutate()}
              loading={sign.isLoading}
              className="ui-test-d31d1806"
            >
              <SigningButtonInner>
                <span>{lang.debtorWebPaymentPlans.agreement}</span>
                <ArrowRight />
              </SigningButtonInner>
            </Button>
          </SigningCta>
        </SigningContent>
      </Panel>
    </>
  )
}

export default SigningStep

const SigningContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 8px 4px 4px;
  text-align: center;
`

const SigningIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.04);
  color: #333;

  svg {
    width: 28px;
    height: 28px;
  }
`

const SigningText = styled.p`
  margin: 0;
  max-width: 480px;
  font-size: 15px;
  line-height: 1.55;
  color: #444;
`

const CollectorName = styled.strong`
  color: #111;
  font-weight: 700;
`

const SigningCta = styled.div`
  width: 100%;
  max-width: 380px;
  margin-top: 4px;
`

const SigningButtonInner = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.15s ease;
  }

  button:hover & svg {
    transform: translateX(3px);
  }
`

const BackLink = styled.button`
  display: none;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 16px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #555;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    transform 0.05s ease;

  @media (max-width: 768px) {
    display: inline-flex;
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    transition: transform 0.15s ease;
  }

  &:hover:not(:disabled) {
    background: #fafafa;
    border-color: #bbb;
    color: #222;

    svg {
      transform: translateX(-2px);
    }
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:focus-visible {
    outline: none;
    border-color: #888;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
